import type { DocumentV1 } from '@alpod-ai-writer/backend';
import type { CursorData } from '@alpod-ai-writer/shared';
import { type DocumentTemplate, type DocumentTypeOptions } from '@alpod-ai-writer/shared';
import clsx from 'clsx';
import { CheckCheckIcon,FileDownIcon,HistoryIcon,LifeBuoyIcon,ListIcon,LockIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';

import { AppHeaderButton } from '../../app/components/appHeader/appHeaderButton.component';
import { DocumentSyncStatus } from '../../app/components/document/documentSyncStatus.component';
import { JobList } from '../../app/components/jobList/jobList.component';
import { Loading } from '../../components/loading/loading.component';
import { ConfirmationModal } from '../../components/modals/confirmation/confirmationModal.component';
import { useLocalStorage } from '../../hooks/useLocalStorage.hook';
import { useModals } from '../../hooks/useModals.hook';
import { ScreenSize } from '../../lib/responsivity.utils';
import { DocumentEditorContext } from '../contexts/documentEditor.context';
import { useDocumentStructure } from '../hooks/useDocumentStructure';
import { useRevisionContent } from '../hooks/useRevisionContent.hook';
import { useScreenSize } from '../hooks/useScreenSize.hook';
import { useSectionNav } from '../hooks/useSectionNav.hook';
import { type LastSaveMeta, type YJSProviderHookResult } from '../hooks/useYjsProvider.hook';
import type { EditorSection } from '../types/editorSection.types';
import { acceptAllSuggestionsInDocument } from '../utils/acceptAllSuggestionsInDocument';
import { formatDateTime } from '../utils/string.utils';
import { DocumentProgress } from './common/documentProgress.component';
import { RevisionDisclaimer } from './common/revisionDisclaimer.component';
import { DocumentImportSection } from './documentImportSection.component';
import { DocumentIntroSection } from './documentIntroSection.component';
import { DocumentOutline } from './documentOutline.component';
import { DocumentRevisionList } from './documentRevisionList.component';
import { DocumentSection } from './documentSection.component';
import { DocumentSettingsSection } from './documentSettingsSection.component';
import { DocumentUnlockSection } from './documentUnlockSection.component';
import { ToolbarButton } from './toolbar/toolbarButton.component';

const SETTINGS_SECTION_ID = 'settings';
const INTRO_SECTION_ID = 'intro';
const UNLOCK_SECTION_ID = 'payment';
const IMPORT_SECTION_ID = 'import';

const SpecialSectionList = [ SETTINGS_SECTION_ID, INTRO_SECTION_ID, UNLOCK_SECTION_ID, IMPORT_SECTION_ID ];

export const SHOW_RIGHT_PANEL_KEY = 'editor-show-right-panel';

function pluralizeKorektury(count: number): string {
    if (count === 1) return 'je 1 korektura';
    if (count >= 2 && count <= 4) return `jsou ${count} korektury`;
    return `je ${count} korektur`;
}

export interface DocumentEditorProps {
    template: DocumentTemplate;
    document: DocumentV1.Record;
    currentRevisionId: string;
    yjsProviderState: YJSProviderHookResult;
    importYjsProviderState: YJSProviderHookResult;
    cursorData: CursorData;
    renderSettings: () => React.ReactNode;
    renderUnlock: () => React.ReactNode;
    onDocumentExport: () => void;
    onRequestHelp: (section?: string, fields?: Record<string, string>) => void;
}

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
    template,
    document,
    currentRevisionId,
    yjsProviderState,
    importYjsProviderState,
    cursorData,
    renderSettings,
    renderUnlock,
    onDocumentExport,
    onRequestHelp
}) => {
    const isReadOnly = !document?.canEdit;
    const isUnlocked = !!document?.paidForAt;

    const { provider, wasSynced } = yjsProviderState;

    const revisionContent = useRevisionContent(document.documentId, currentRevisionId);

    const specialSections: EditorSection[] = [
        !isUnlocked && { id: UNLOCK_SECTION_ID, title: 'Odemknout dokument' },
        { id: INTRO_SECTION_ID, title: 'Jak psát tento dokument?' },
        { id: SETTINGS_SECTION_ID, title: 'Nastavení dokumentu' },
        isUnlocked && { id: IMPORT_SECTION_ID, title: 'Nahrát práci' }
    ].filter((x): x is EditorSection => !!x);

    const navSections = isUnlocked
        ? [ ...specialSections, ...template.config.sections ]
        : [ ...specialSections ];

    const toolbarRef = useRef<HTMLDivElement | null>(null);
    const [ toolbarEl, setToolbarEl ] = useState<HTMLDivElement | null>(null);

    const { currentSection, isPending, selectSection } = useSectionNav(navSections);

    const [visitedSections, setVisitedSections] = useState<Set<string>>(() => new Set<string>());

    const [showLoading, setShowLoading] = useState(false);
    const [allSectionsReady, setAllSectionsReady] = useState(false);
    const [preloadCount, setPreloadCount] = useState(0);
    const totalSectionCount = template.config.sections.length;

    const [ showOutline, setShowOutline ] = useLocalStorage('editor-show-outline', true);
    const [ showRightPanel, setShowRightPanel ] = useLocalStorage(SHOW_RIGHT_PANEL_KEY, false);

    const docStructure = useDocumentStructure(template, provider?.document);

    const { showModal } = useModals();

    const screenSize = useScreenSize();

    const toggleOutline = useCallback(() => {
        setShowOutline((prev) => !prev);
    }, [ setShowOutline ]);

    const toggleRightPanel = useCallback(() => {
        setShowRightPanel((prev) => !prev);
    },  [ setShowRightPanel ]);

    const exportDocument = useCallback(() => {
        onDocumentExport();
    }, [ provider, onDocumentExport ]);

    const recomputeDocStructure = docStructure.recompute;
    const acceptAllSuggestionsInWholeDocument = useCallback(() => {
        showModal('confirm-accept-all-suggestions', ConfirmationModal, {
            isDanger: true,
            confirmLabel: 'Schválit vše',
            cancelLabel: 'Zrušit',
            message: (
                <>
                    Jste si opravdu jistý/á, že chcete schválit hromadně všechny změny?<br />
                    <strong>POZOR!</strong> Důrazně doporučujeme korektury realizované pomocí AI důsledně projít.
                </>
            ),
            onConfirm: async () => {
                if (provider?.document) {
                    await acceptAllSuggestionsInDocument(template, provider.document);
                    // Bypass useDocumentStructure's 1s debounce so the toolbar
                    // button hides at the same moment the modal closes.
                    recomputeDocStructure();
                }
                return true;
            }
        });
    }, [ showModal, provider, template, recomputeDocStructure ]);

    const requestHelp = useCallback((fields?: Record<string, string>) => {
        const sectionLabel = navSections.find((x) => x.id === currentSection?.sectionId)?.title;
        onRequestHelp(sectionLabel, fields);
    }, [ onRequestHelp, currentSection, navSections ]);

    const resolveUserIdentity = useCallback((identityId: string) => {
        const users = [
            document.ownerIdentity,
            ...document.sharedWith.map(r => r.identity)
        ];

        return users.find(user => user.identityId === identityId) ?? null;
    }, [ document ]);

    useEffect(() => {
        if (provider?.awareness) {
            provider.awareness.setLocalStateField('data', cursorData);
        }
    }, [ provider ]);

    useEffect(() => {
        if (toolbarRef.current && !toolbarEl) {
            setToolbarEl(toolbarRef.current);
        }
    }, [ toolbarEl, provider, wasSynced ]);

    useEffect(() => {
        if (screenSize < ScreenSize.LG) {
            setShowOutline(false);
        }
    }, [ screenSize, currentSection ]);

    useEffect(() => {
        const sectionId = currentSection?.sectionId;
        if (!sectionId || SpecialSectionList.includes(sectionId)) return;
        // During initial preload, wait for revisionContent so preloadedContent is available
        // and sections use cached Slate JSON instead of the slow yTextToSlateElement path.
        // After allSectionsReady, navigation mounts sections freely.
        if (!allSectionsReady && revisionContent === null) return;
        setVisitedSections((prev) => {
            if (prev.has(sectionId)) return prev;
            const next = new Set(prev);
            next.add(sectionId);
            return next;
        });
    }, [ currentSection?.sectionId, revisionContent, allSectionsReady ]);

    useEffect(() => {
        if (!isPending) {
            setShowLoading(false);
            return;
        }
        const t = setTimeout(() => setShowLoading(true), 100);
        return () => clearTimeout(t);
    }, [ isPending ]);

    useEffect(() => {
        if (!wasSynced) return;
        // null means still loading — wait so sections use cached content and won't block the main thread
        if (totalSectionCount > 0 && revisionContent === null) return;

        const sections = template.config.sections;
        if (sections.length === 0) {
            setAllSectionsReady(true);
            return;
        }

        let index = 0;
        let cancelled = false;

        const mountNext = () => {
            if (cancelled) return;

            if (index >= sections.length) {
                setTimeout(() => {
                    if (!cancelled) setAllSectionsReady(true);
                }, 50);
                return;
            }

            const s = sections[index];
            const currentIndex = index;
            index++;

            setPreloadCount(currentIndex + 1);
            setVisitedSections((prev) => {
                if (prev.has(s.id)) return prev;
                const next = new Set(prev);
                next.add(s.id);
                return next;
            });

            setTimeout(mountNext, 50);
        };

        mountNext();

        return () => { cancelled = true; };
    }, [ wasSynced, revisionContent ]);

    const lastSaveMeta: LastSaveMeta|null = yjsProviderState.lastSaveMeta ?? {
        timestamp: document.currentRevisionUpdatedAt
            ? (new Date(document.currentRevisionUpdatedAt)).getTime()
            : null,
        changedBy: document.currentRevisionUpdatedBySnippet
            ? {
                identityId: document.currentRevisionUpdatedBySnippet.identityId,
                name: document.currentRevisionUpdatedBySnippet.name,
                email: document.currentRevisionUpdatedBySnippet.email
            }
            : null
    };

    if (!provider || !wasSynced) {
        return (
            <div className='flex flex-row items-center justify-center min-h-screen'>
                <Loading label='Načítání dokumentu...' />
            </div>
        );
    }

    const docTypeConfig: DocumentTypeOptions|undefined = template.config.types[document.documentType];

    return (
        <>
        {!allSectionsReady && (
            <div className='fixed inset-0 z-[200] bg-white flex flex-col items-center justify-center gap-4'>
                <Loading label='Připravuji dokument...' />
                <div className='flex flex-col items-center gap-2'>
                    <p className='text-sm text-gray-400'>{Math.min(preloadCount, totalSectionCount)} / {totalSectionCount} sekcí</p>
                    <div className='w-48 h-1.5 bg-gray-200 rounded-full overflow-hidden'>
                        <div
                            className='h-full bg-violet-500 transition-all duration-75 ease-linear'
                            style={{ width: `${totalSectionCount > 0 ? (Math.min(preloadCount, totalSectionCount) / totalSectionCount) * 100 : 0}%` }}
                        />
                    </div>
                </div>
            </div>
        )}
        <DocumentEditorContext.Provider value={{
            document,
            template,
            yjsProvider: provider,
            currentRevisionId,
            cursorData,
            readOnly: isReadOnly,
            isUnlocked: isUnlocked,
            requestHelp,
            resolveUserIdentity
        }}>
            <div className='fixed top-14 w-full lg:px-2 z-50'>
                <div className={clsx(
                    'toolbar-bar flex flex-row items-center justify-center gap-2 bg-gray-200',
                    'lg:rounded-full p-2 lg:p-1.5'
                )}>
                    <div className='flex flex-row items-center gap-2 shrink-0'>
                        <ToolbarButton
                            rounded
                            style='primary-toggle'
                            size='lg'
                            isActive={showOutline}
                            onClick={toggleOutline}
                        ><ListIcon size={18} /> <span className='btn-label-osnova'>Osnova</span></ToolbarButton>
                        <DocumentSyncStatus
                            provider={yjsProviderState}
                            className='flex lg:hidden flex-shrink overflow-hidden'
                            labelClass='hidden sm:inline-block'
                        />
                    </div>
                    <div
                        className={clsx(
                            'grow flex flex-row items-center gap-2',
                            'fixed left-0 right-0 bottom-0 bg-gray-200 p-2 overflow-y-hidden overflow-x-auto justify-start lg:justify-center',
                            'toolbar-center lg:relative lg:left-auto lg:right-auto lg:bottom-auto lg:p-0 lg:bg-transparent lg:justify-start lg:overflow-x-auto lg:min-w-0'
                        )}
                        ref={toolbarRef}
                    ></div>
                    <div className='flex-grow lg:hidden' />
                    <div className='flex flex-row items-center justify-end gap-1.5 shrink-0'>
                        <ToolbarButton
                            style='default'
                            size='lg'
                            rounded
                            isActive={showRightPanel}
                            onClick={toggleRightPanel}
                            iconOnly
                            title='Zobrazit revize'
                        ><HistoryIcon size={18} /></ToolbarButton>
                        <ToolbarButton
                            rounded
                            style='primary-help'
                            size='lg'
                            onClick={() => requestHelp()}
                        ><LifeBuoyIcon size={18} /> <span className='btn-label-chci'>Chci se poradit</span></ToolbarButton>
                        {/* <ToolbarButton
                            rounded
                            style='feature-accent'
                            size='lg'
                            isActive={showAiChat}
                            onClick={toggleAiChat}
                        ><SparklesIcon size={18} /> <span className='hidden xl:block'>AI Asistent</span></ToolbarButton> */}
                    </div>
                </div>
            </div>
            <div className="flex flex-row items-start justify-center min-h-screen pt-[5.5rem] gap-0">
                {showOutline && (
                    <div className={clsx(
                        'overflow-x-hidden overflow-y-visible flex flex-col gap-4 py-4 md:py-8',
                        'fixed left-0 top-[6.75rem] bottom-0 w-full bg-gray-100 z-[55]',
                        'md:sticky md:grow md:left-[unset] md:top-24 md:bottom-[unset] md:w-auto md:min-w-[16rem] md:max-w-[22rem] md:max-h-[calc(100vh-6rem)] md:bg-transparent md:z-0',
                        // 'sticky top-24 grow min-w-[16rem] max-w-[24rem] max-h-[calc(100vh-6rem)]',
                        showRightPanel ? 'px-4 xl:px-6' : 'px-4 lg:pr-6 xl:pl-0'
                    )}>
                        <div className='text-sm font-semibold text-gray-400 px-4 shrink-0'>Osnova dokumentu</div>
                        <DocumentOutline
                            template={template}
                            specialSections={specialSections}
                            currentSection={currentSection}
                            structure={docStructure}
                            onSelect={selectSection}
                            isUnlocked={isUnlocked}
                            className='grow overflow-x-hidden overflow-y-auto pr-4'
                        />
                        <div className='px-4 md:max-w-[16rem]'>
                            <DocumentProgress
                                characterCount={docStructure.totalCharacters}
                                minCharacterCount={(docTypeConfig?.minPages ?? 0) * 1800}
                                maxCharacterCount={docTypeConfig?.maxPages ? (docTypeConfig.maxPages * 1800) : null}
                                normPageCharacterCount={1800}
                            />
                        </div>
                        <div className='shrink-0 flex flex-col items-start justify-start gap-1 px-4'>
                            <AppHeaderButton
                                style='primary'
                                size='lg'
                                responsive={false}
                                onClick={exportDocument}
                                disabled={!isUnlocked}
                            >
                                {isUnlocked && (
                                    <FileDownIcon size={18} />
                                )}
                                {!isUnlocked && (
                                    <LockIcon size={18} />
                                )}
                                <span className='block'>Stáhnout dokument</span>
                            </AppHeaderButton>
                        </div>
                        <div className='empty:hidden pt-2'>
                            {lastSaveMeta?.changedBy && (
                                <p className='text-xs text-gray-600 px-4 leading-relaxed'>
                                    Naposledy upraveno <span className='font-medium'>{formatDateTime(lastSaveMeta.timestamp)}</span><br />uživatelem <span className='font-medium'>{lastSaveMeta.changedBy.name}</span>
                                </p>
                            )}
                        </div>
                    </div>
                )}
                <div className="flex flex-col justify-center items-start w-full max-w-[50rem] flex-grow py-8 lg:px-8">
                    <h2 className="text-center text-2xl md:text-2xl font-medium text-gray-600 w-full mb-3">{document.documentName}</h2>

                    {!isReadOnly && docStructure.totalSuggestionCount > 0 && (
                        <div className='w-full mb-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-4 py-3 rounded-lg border border-blue-200 bg-blue-50'>
                            <p className='text-sm text-gray-700'>
                                V této verzi dokumentu {pluralizeKorektury(docStructure.totalSuggestionCount)}.
                            </p>
                            <button
                                type='button'
                                onClick={acceptAllSuggestionsInWholeDocument}
                                className='inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-lime-600 hover:bg-lime-700 text-white text-sm font-medium transition-colors duration-200 shrink-0'
                            >
                                <CheckCheckIcon size={16} className='flex-shrink-0' />
                                <span>Schválit všechny změny</span>
                            </button>
                        </div>
                    )}

                    <JobList document={document} />

                    {currentSection?.sectionId === INTRO_SECTION_ID && (
                        <DocumentIntroSection
                            className='w-full py-3'
                        />
                    )}

                    {currentSection?.sectionId === SETTINGS_SECTION_ID && (
                        <DocumentSettingsSection
                            renderSettings={renderSettings}
                            className='w-full py-3'
                        />
                    )}

                    {currentSection?.sectionId === UNLOCK_SECTION_ID && (
                        <DocumentUnlockSection
                            renderUnlock={renderUnlock}
                            className='w-full py-3'
                        />
                    )}

                    {currentSection?.sectionId === IMPORT_SECTION_ID && importYjsProviderState.provider && (
                        <DocumentImportSection
                            yDoc={importYjsProviderState.provider.document}
                            documentField='import'
                            cursorData={cursorData}
                            toolbarEl={toolbarEl}
                            className='w-full py-3'
                        />
                    )}

                    {showLoading && !SpecialSectionList.includes(currentSection?.sectionId ?? '') && (
                        <div className='w-full py-16 flex items-center justify-center'>
                            <Loading label='Načítání sekce...' />
                        </div>
                    )}

                    {template.config.sections
                        .filter((s) => visitedSections.has(s.id))
                        .map((s) => {
                            const isActive = currentSection?.sectionId === s.id;
                            return (
                                <div
                                    key={s.id}
                                    className='w-full'
                                    style={isActive ? undefined : { display: 'none' }}
                                    aria-hidden={!isActive}
                                >
                                    <DocumentSection
                                        className='w-full py-3'
                                        template={template}
                                        yDoc={provider.document}
                                        currentSection={s.id}
                                        cursorData={cursorData}
                                        toolbarEl={isActive ? toolbarEl : null}
                                        readOnly={isReadOnly}
                                        revisionContent={revisionContent}
                                    />
                                </div>
                            );
                        })
                    }
                </div>
                {showRightPanel && (
                    <div className={clsx(
                        'overflow-x-hidden overflow-y-auto py-4 md:py-8 flex flex-col items-end gap-4',
                        'fixed top-[6.75rem] right-0 bottom-0 w-full bg-gray-100 z-50',
                        'md:top-[6.5rem] md:max-w-[30rem] md:backdrop-blur-lg md:bg-opacity-80 md:shadow-2xl',
                        'xl:sticky xl:top-24 xl:right-auto xl:bottom-auto xl:flex-shrink-0 xl:w-[40%] xl:max-w-[22rem] md:max-h-[calc(100vh-6rem)] xl:bg-transparent xl:z-0 xl:shadow-none xl:backdrop-filter-none',
                        // 'sticky top-24 w-[40%] max-w-[50rem]',
                        showOutline ? 'px-4 xl:px-6' : 'px-4 xl:pl-6 xl:pr-0'
                    )}>
                        <div className='flex flex-col gap-4 w-full max-w-[16rem]'>
                            {document.currentRevisionId !== currentRevisionId && (
                                <RevisionDisclaimer
                                    documentId={document.documentId}
                                    revisionId={currentRevisionId}
                                />
                            )}
                            <div className='text-sm font-semibold text-gray-400 px-4 shrink-0'>Revize dokumentu</div>
                            <DocumentRevisionList
                                document={document}
                                currentRevisionId={currentRevisionId}
                                currentSection={currentSection}
                            />
                        </div>

                        {/* <div className='bg-gray-200 rounded-md flex-shrink-0 w-full h-[10rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-[75%] h-[3rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-full h-[8rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-[75%] h-[3rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-full h-[5rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-[75%] h-[3rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-full h-[5rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-[75%] h-[3rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-full h-[5rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-[75%] h-[3rem]'></div>
                        <div className='bg-gray-200 rounded-md flex-shrink-0 w-full h-[5rem]'></div> */}
                    </div>
                )}
                {!showRightPanel && showOutline && (
                    <div className='hidden md:block md:w-4 xl:w-[5%]' />
                )}
            </div>
        </DocumentEditorContext.Provider>
        </>
    );
};
